import React, { useState } from "react";
import { Tabs, Spin, Table, Modal } from "antd";
import { useQuery } from "@apollo/client";
import { INTEGRATION_RECORD_COUNT_QUERY, DATA_IMPORT_RECORDS_QUERY } from "~/graphql/query";
import { compareAsc } from "date-fns";
import styles from "./reports-page.css";
import { FilterFilled } from "@ant-design/icons";
import { DateRangeFilter } from "../../../components/date-range-filter";
import { FilterDropdownProps } from "antd/es/table/interface";
import { DataImportEntityType } from "~/gql/graphql";
import { ColumnType } from "antd/es/table";
import { TablePaginationConfig } from "antd/es/table";
import { FilterValue } from "antd/es/table/interface";

const {
  ["loading-overlay"]: loadingOverlayClassName,
  ["spinner-container"]: spinnerContainerClassName,
  ["tab-content"]: tabContentClassName,
} = styles as any;

const { TabPane } = Tabs;

const relevantEntityTypes = [
  DataImportEntityType.OrganisationalUnit,
  DataImportEntityType.OfficeLocation,
  DataImportEntityType.FeeEarnerLevel,
  DataImportEntityType.FeeEarner,
  DataImportEntityType.User,
  DataImportEntityType.Client,
  DataImportEntityType.Rate,
  DataImportEntityType.ExternalMatter,
  DataImportEntityType.TimeEntry,
  DataImportEntityType.MatterPermissions,
  DataImportEntityType.MatterInvoice,
  DataImportEntityType.Currency,
];

export const IntegrationReportsPage = () => {
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [currentErrorMessage, setCurrentErrorMessage] = useState("");
  const [dateRange, setDateRange] = useState<[Date | null, Date | null]>([null, null]);
  const [entityTypeFilters, setEntityTypeFilters] = useState<DataImportEntityType[]>([]);

  const { data: integrationRecordCount, loading: integrationRecordCountLoading } = useQuery(
    INTEGRATION_RECORD_COUNT_QUERY
  );

  const { data: dataImportRecords, loading: dataImportRecordsLoading } = useQuery(
    DATA_IMPORT_RECORDS_QUERY,
    {
      variables: {
        input: {
          page: currentPage,
          pageSize: pageSize,
          startDate: dateRange[0] ? dateRange[0].toISOString() : undefined,
          endDate: dateRange[1] ? dateRange[1].toISOString() : undefined,
          entityTypes: entityTypeFilters,
        },
      },
    }
  );

  const recordCountColumns = [
    {
      title: "Entity",
      dataIndex: "entity",
      key: "entity",
    },
    {
      title: "Count",
      dataIndex: "count",
      key: "count",
    },
  ];

  const recordCountData = integrationRecordCount?.integrationRecordCountQuery
    ? Object.entries(integrationRecordCount.integrationRecordCountQuery)
        .filter(([key]) => key !== "__typename")
        .map(([key, value]) => ({
          key,
          entity: key
            .replace(/([A-Z])/g, " $1")
            .replace(/^./, (str) => str.toUpperCase())
            .trim(),
          count: value,
        }))
    : [];

  const dataImportColumns: ColumnType<any>[] = [
    {
      title: "Entity Type",
      dataIndex: "entityType",
      key: "entityType",
      filterIcon: <FilterFilled style={{ fontSize: 18 }} />,
      filters: relevantEntityTypes.map((value) => ({
        text: value,
        value,
      })),
      filteredValue: entityTypeFilters,
      onFilter: (value: string | number | boolean, record: any) =>
        record.entityType === value.toString(),
    },
    {
      title: "Date / Time",
      dataIndex: "createdAt",
      key: "createdAt",
      render: (text: string) => new Date(text).toLocaleString(),
      filterIcon: <FilterFilled style={{ fontSize: 18 }} />,
      filteredValue: dateRange
        .filter((date): date is Date => date !== null)
        .map((date) => date.getDate()),
      filterDropdown: (props: FilterDropdownProps) =>
        filterCreatedAt({
          ...props,
          setDateRange: (range: [Date | null, Date | null]) => {
            setDateRange(range);
            setCurrentPage(1);
          },
        }),
      sorter: (a: any, b: any) => compareAsc(new Date(a.createdAt), new Date(b.createdAt)),
    },
    {
      title: "Created",
      dataIndex: "createdCount",
      key: "createdCount",
    },
    {
      title: "Updated",
      dataIndex: "updatedCount",
      key: "updatedCount",
    },
    {
      title: "Deleted",
      dataIndex: "deletedCount",
      key: "deletedCount",
    },
    {
      title: "Error Message",
      dataIndex: "errorMessage",
      key: "errorMessage",
      render: (text: string) => {
        if (!text || text.trim() === "") {
          return "None";
        }
        return (
          <a onClick={() => showModal(text)}>
            {text.length > 50 ? `${text.substring(0, 50)}...` : text}
          </a>
        );
      },
    },
  ];

  const showModal = (errorMessage: string) => {
    setCurrentErrorMessage(errorMessage);
    setIsModalVisible(true);
  };

  const handleModalClose = () => {
    setIsModalVisible(false);
  };

  const handleTableChange = (
    pagination: TablePaginationConfig,
    filters: Record<string, FilterValue | null>,
    sorter: any
  ) => {
    setCurrentPage(pagination.current || 1);
    setPageSize(pagination.pageSize || 10);
    setEntityTypeFilters((filters.entityType as DataImportEntityType[]) || []);
  };

  return (
    <div>
      <div style={{ position: "relative" }}>
        {(integrationRecordCountLoading || dataImportRecordsLoading) && (
          <div className={loadingOverlayClassName}>
            <div className={spinnerContainerClassName}>
              <Spin />
            </div>
          </div>
        )}
        <Tabs defaultActiveKey="recordCounts">
          <TabPane tab="Record Counts" key="recordCounts">
            <div className={tabContentClassName}>
              <Table columns={recordCountColumns} dataSource={recordCountData} pagination={false} />
            </div>
          </TabPane>
          <TabPane tab="Data Import" key="dataImport">
            <div className={tabContentClassName}>
              <Table
                columns={dataImportColumns}
                dataSource={dataImportRecords?.dataImportRecordsQuery.items}
                pagination={{
                  current: currentPage,
                  pageSize: pageSize,
                  total: dataImportRecords?.dataImportRecordsQuery.total,
                }}
                onChange={handleTableChange}
              />
            </div>
          </TabPane>
        </Tabs>
      </div>
      <Modal
        title="Error Message"
        width={600}
        open={isModalVisible}
        onOk={handleModalClose}
        onCancel={handleModalClose}
        footer={null}
        bodyStyle={{
          maxHeight: "60vh",
          display: "flex",
          flexDirection: "column",
        }}
      >
        <div style={{ flex: 1, overflow: "auto" }}>
          <p>{currentErrorMessage}</p>
        </div>
      </Modal>
    </div>
  );
};

const NULL_SELECTION: number[] = [];

interface ExtendedFilterDropdownProps {
  setSelectedKeys: (selectedKeys: React.Key[]) => void;
  confirm: () => void;
  clearFilters?: () => void;
  setDateRange: (range: [Date | null, Date | null]) => void;
}

export const filterCreatedAt = ({
  setSelectedKeys,
  confirm,
  clearFilters,
  setDateRange,
}: ExtendedFilterDropdownProps) => (
  <DateRangeFilter
    onApplyFilter={(value) => {
      if (value && value.from && value.to) {
        const fromTime = value.from.getTime();
        const toTime = value.to.getTime();
        setSelectedKeys([fromTime, toTime]);
        setDateRange([value.from, value.to]);
      } else {
        setSelectedKeys(NULL_SELECTION);
        setDateRange([null, null]);
      }
      confirm();
    }}
    onClearFilter={() => {
      clearFilters && clearFilters();
      setDateRange([null, null]);
      confirm();
    }}
  />
);
